import { createAction } from '@reduxjs/toolkit'
import { CancelTokenSource } from 'axios'
import { call, cancel, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects'

import { fetchProjectSuccess } from '../../../actions/drawable'
import { getProjectById } from '../../../api/projects-api'
import { getTakeoffData } from '../../../api/takeoff-api'
import { Project } from '../../../models/project'
import { IntermediateTakeoffResponse, PreparedTakeoff, TakeOffResponse } from '../../../models/takeoff'
import { getFormattedDatetime } from '../../../utils/datetime'
import { setTakeoffData } from '../../slices/2D'
import {
    selectLastLoadingTime,
    setIsTakeOffLoading,
    setLastLoadingTime,
    setPageDataLoading,
} from '../../slices/loading'
import { selectProject } from '../../slices/project'

export const fetchTakeoffData = createAction<{ projectId: number; timeout: number; cancelSource: CancelTokenSource }>(
    'fetchTakeoffData'
)
export const stopPollingForNewTakeoffData = createAction('stopFetchingTakeoffData')

const BUILDING_NAME_GROUPING_KEY = 'buildingName'
const CORE_PACKAGE_GROUPING_KEY = 'corePackage'
const TIMEOUT_IN_MS = 10000

// Function to group the array by 'field1' and 'field2'
function groupByTwoFields(
    array: Omit<IntermediateTakeoffResponse, 'measurementFile'>[],
    field1: string,
    field2: string
) {
    const groupedByField1 = array.reduce((result, item) => {
        const key1 = item[field1]

        if (!result[key1]) {
            result[key1] = {} as Record<string, Omit<IntermediateTakeoffResponse, 'measurementFile'>[]>
        }

        return result
    }, {} as Record<string, Record<string, Omit<IntermediateTakeoffResponse, 'measurementFile'>[]>>)

    return array.reduce((result, item) => {
        const key1 = item[field1]
        const key2 = item[field2]

        if (!result[key1][key2]) {
            result[key1][key2] = []
        }
        result[key1][key2].push(item)

        return result
    }, groupedByField1)
}

function finalPrepareTakeoffData(groupedArray) {
    const groupedUpdatedTakeoffData: {
        description: string
        quantity?: string
        sku?: string
        isBuilding?: boolean
        isCorePackage?: boolean
    }[] = []

    Object.keys(groupedArray).forEach((buildingName) => {
        // get the building name for row
        groupedUpdatedTakeoffData.push({ description: buildingName, isBuilding: true })

        Object.keys(groupedArray[buildingName]).forEach((corePackage) => {
            // get the core package name for row
            groupedUpdatedTakeoffData.push({ description: corePackage, isCorePackage: true })

            groupedArray[buildingName][corePackage].forEach((item) => {
                // get the needed data for row
                groupedUpdatedTakeoffData.push({
                    description: item.description,
                    quantity: item.quantity,
                    sku: item.sku,
                })
            })
        })
    })

    return groupedUpdatedTakeoffData
}

function groupByMeasurementFile(data: IntermediateTakeoffResponse[]) {
    return data.reduce((acc, obj) => {
        const { measurementFile, ...rest } = obj

        if (!acc[measurementFile]) {
            acc[measurementFile] = []
        }
        acc[measurementFile].push(rest)

        return acc
    }, {} as Record<string, Omit<IntermediateTakeoffResponse, 'measurementFile'>[]>)
}

export function* handleFetchTakeoffData() {
    const quotePollTask = yield fork(pollForTakeoffData)

    yield take(stopPollingForNewTakeoffData.type)
    yield cancel(quotePollTask)
}

export function prepareTakeoffData(takeOffData: TakeOffResponse): PreparedTakeoff[] {
    const preparedNewTakeoffData: IntermediateTakeoffResponse[] = takeOffData.data.AllParts.reduce((parts, part) => {
        if (part.Quantity > 0) {
            parts.push({
                buildingName: part.BuildingName,
                corePackage: part.CorePackage,
                measurementFile: part.MeasurementFile,
                description: part.Description,
                quantity: part.Quantity,
                sku: part.SKU,
            })
        }

        return parts
    }, [] as IntermediateTakeoffResponse[])

    const takeoffDataGropedByMeasurementFile = groupByMeasurementFile(preparedNewTakeoffData)

    const groupDataInsideMeasureFiles: Record<
        string,
        Record<string, Record<string, Omit<IntermediateTakeoffResponse, 'measurementFile'>[]>>
    > = {}

    Object.keys(takeoffDataGropedByMeasurementFile).forEach((measureFileName) => {
        groupDataInsideMeasureFiles[measureFileName] = groupByTwoFields(
            takeoffDataGropedByMeasurementFile[measureFileName],
            BUILDING_NAME_GROUPING_KEY,
            CORE_PACKAGE_GROUPING_KEY
        )
    })

    const finalGroupedData: PreparedTakeoff[] = []

    Object.keys(groupDataInsideMeasureFiles).forEach((measureFileName) => {
        const res = finalPrepareTakeoffData(groupDataInsideMeasureFiles[measureFileName])

        finalGroupedData.push({ description: measureFileName, isMeasureFile: true }, ...res)
    })

    return finalGroupedData
}

export function* stopPollingAndUpdateData(
    generatedTakeoff: TakeOffResponse & {
        generated_at: number
    },
    newFormattedProjectTime: string,
    projectId: number
) {
    const preparedData: PreparedTakeoff[] = yield call(prepareTakeoffData, generatedTakeoff)

    yield put(setTakeoffData({ projectId: projectId, takeoffData: preparedData }))
    yield put(setLastLoadingTime(newFormattedProjectTime))
    yield put(setIsTakeOffLoading(false))
    yield put(setPageDataLoading(false))
    yield put(stopPollingForNewTakeoffData())
}

export function* pollForTakeoffData() {
    yield put(setPageDataLoading(true))
    yield put(setIsTakeOffLoading(false))

    const project: Project | null = yield select(selectProject)

    if (!project) {
        yield put(setPageDataLoading(false))
        yield put(stopPollingForNewTakeoffData())

        return
    }

    // Takeoff data already exists — display it and stop
    if (project.outputs?.generated_takeoff) {
        const formattedProjectTime = getFormattedDatetime(
            project.outputs.generated_takeoff.generated_at,
            'YYYY-MM-DD H:mm:ss'
        )

        yield call(stopPollingAndUpdateData, project.outputs.generated_takeoff, formattedProjectTime, project.id)

        return
    }

    // Errors exist — stop and let UI display them
    if (project.outputs?.errors) {
        yield put(setPageDataLoading(false))
        yield put(stopPollingForNewTakeoffData())

        return
    }

    // No takeoff, no errors — trigger generation and poll for results
    yield call(getTakeoffData, project.id, undefined)
    yield put(setIsTakeOffLoading(true))

    while (true) {
        yield delay(TIMEOUT_IN_MS)

        try {
            const newProjectData: Project = yield call(getProjectById, project.id)

            yield put(fetchProjectSuccess(newProjectData))

            // Backend returned errors — stop polling
            if (newProjectData.outputs?.errors) {
                yield put(setIsTakeOffLoading(false))
                yield put(setPageDataLoading(false))
                yield put(stopPollingForNewTakeoffData())

                return
            }

            // Takeoff data is ready — display it and stop
            if (newProjectData.outputs?.generated_takeoff?.generated_at) {
                const lastUpdatedTime: string | null = yield select(selectLastLoadingTime)
                const newFormattedProjectTime = getFormattedDatetime(
                    newProjectData.outputs.generated_takeoff.generated_at,
                    'YYYY-MM-DD H:mm:ss'
                )

                if (newFormattedProjectTime !== lastUpdatedTime) {
                    yield call(
                        stopPollingAndUpdateData,
                        newProjectData.outputs.generated_takeoff,
                        newFormattedProjectTime,
                        project.id
                    )

                    return
                }
            }
        } catch (error) {
            console.error('pollForTakeoffData error:', error)
            yield put(setIsTakeOffLoading(false))
            yield put(setPageDataLoading(false))
            yield put(stopPollingForNewTakeoffData())

            return
        }
    }
}

export function* watchForFetchTakeoffData() {
    yield takeLatest(fetchTakeoffData.type, handleFetchTakeoffData)
}
